import { Box, FelaCSS } from '@bridebook/ui';
import componentStyles from './highlights.style';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import {
  getIsVenueSupplierAccomodationRooms,
  getSpecialOffers,
  getSupplierCurrencyCode,
  getSupplierPricingCategory,
  getVenuePackages,
  getVenueSupplierDiningCapacity,
} from '@bridebook/web/app-basic/lib/supplier/selectors';
import { PiggyBank, UserTriple, BedHeart, Tag } from '@bridebook/aura/src/icons';
import {
  getPricingValueString,
  isSupplierWithPricingDetails,
} from '@bridebook/web/lib/supplier/utils';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { getIsDesktop } from '@bridebook/web/app-basic/lib/app/selectors';
import { useMemo, FC } from 'react';
import { useTranslation } from 'next-i18next';
import mergeStyles from '@bridebook/ui/src/fela-utils/merge-styles';

interface IProps {
  styleOverrides?: {
    wrapper?: FelaCSS;
    itemsContainer?: FelaCSS;
    itemText?: FelaCSS;
    iconBox?: FelaCSS;
  };
}

const Highlights: FC<IProps> = ({ styleOverrides }) => {
  const { t } = useTranslation('supplier');
  const market = useMarket();

  const isDesktop = useSelector(getIsDesktop);

  const specialOffers = useSelector(getSpecialOffers);
  const diningCapacity = useSelector(getVenueSupplierDiningCapacity);
  const accommodationRooms = useSelector(getIsVenueSupplierAccomodationRooms);
  const packages = useSelector(getVenuePackages);
  const supplierPricingCategory = useSelector(getSupplierPricingCategory);
  const currencyCode = useSelector(getSupplierCurrencyCode);
  const styles = componentStyles();

  const hasPricingDetails = isSupplierWithPricingDetails(packages);
  const dealsCount = useMemo(
    () => specialOffers?.filter((o) => o && o.type).length || 0,
    [specialOffers],
  );

  const price = getPricingValueString(
    currencyCode,
    market,
    packages,
    supplierPricingCategory,
    true,
  );

  const highlightItems = useMemo(
    () =>
      [
        {
          Icon: PiggyBank,
          text: `${t('supplier.accordion.highlights.price')} ${price}`,
          condition: hasPricingDetails,
        },
        {
          Icon: UserTriple,
          text: `${t('supplier.accordion.highlights.guests')} ${diningCapacity}`,
          condition: diningCapacity,
        },
        {
          Icon: BedHeart,
          text: `${t('supplier.accordion.highlights.bedrooms')} ${accommodationRooms}`,
          condition: accommodationRooms > 0,
        },
        {
          Icon: Tag,
          text: `${t('supplier.accordion.highlights.deals')} ${dealsCount}`,
          condition: dealsCount > 0,
        },
      ].filter((item) => item.condition),
    [hasPricingDetails, price, diningCapacity, accommodationRooms, dealsCount, t],
  );

  if (highlightItems.length === 0) return null;

  return (
    <Box as="section" style={mergeStyles([styles.wrapper, styleOverrides?.wrapper || {}])}>
      <Box style={mergeStyles([styles.itemsContainer, styleOverrides?.itemsContainer || {}])}>
        {highlightItems.map(({ Icon, text }) => (
          <Box style={styles.item} key={text}>
            <Box style={mergeStyles([styles.iconBox, styleOverrides?.iconBox || {}])}>
              <Icon width={isDesktop ? 24 : 16} height={isDesktop ? 24 : 16} />
            </Box>
            <Box as="span" style={mergeStyles([styles.itemText, styleOverrides?.itemText || {}])}>
              {text}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default Highlights;
